/**
 * Shared configuration loading utilities for oportal
 * Loads API configuration from config file (production) or environment variables (development)
 */
import { logger } from "@/infrastructure/services/LoggerService";
/** A positive integer, or its decimal string; anything else is `undefined`. */
export function parseEditorImageMaxBytes(value) {
    const parsed = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
    return typeof parsed === "number" && Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}
/**
 * Parses the orbitalEnabled flag from its wire value. The runtime config file
 * is generated by init.sh from an env var, so the value arrives as a string
 * ("true" / "false" / "" when the var is unset).
 */
export function parseOrbitalEnabled(value) {
    return parseBooleanFlag(value, true);
}
/** Same opt-out-only semantics as `parseOrbitalEnabled`, on the bundle value. */
export function parseAppointmentPlanningEnabled(value) {
    return parseBooleanFlag(value, true);
}
/** Opt-in: only an explicit `true` enables. */
export function parseSpontaneousDonorsEnabled(value) {
    return parseBooleanFlag(value, false);
}
/** Opt-in: only an explicit `true` enables. */
export function parseBusinessRolesMockEnabled(value) {
    return parseBooleanFlag(value, false);
}
/**
 * A boolean, or its trimmed case-insensitive `"true"`/`"false"` spelling; anything else — absent,
 * blank, garbage — is `fallback`, the flag's default.
 */
function parseBooleanFlag(value, fallback) {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true")
            return true;
        if (normalized === "false")
            return false;
    }
    return fallback;
}
/**
 * TEMPORARY (OP-6808) — parses the sidebar allow-list from its wire value.
 * The runtime config file is generated by init.sh from an env var, so the list
 * normally arrives as a comma-separated string; a hand-written apiConfig.json may
 * use a real array. Returns `undefined` when nothing usable is configured, so an
 * unset variable means "no filtering" rather than "empty menu".
 */
export function parseMenuVisibleKeys(value) {
    const raw = Array.isArray(value)
        ? value
        : typeof value === "string"
            ? value.split(",")
            : [];
    const keys = raw
        .filter((entry) => typeof entry === "string")
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
    return keys.length > 0 ? keys : undefined;
}
/**
 * TEMPORARY (OP-6808) — parses the configured landing page. Syntactic only: a
 * value that is not a bare absolute path (no query, no fragment) falls back to
 * "/". Whether the path resolves to a registered route needs the Router and is
 * checked by `resolveUsableLandingTarget` in shared/utils/landingPage.ts.
 *
 * `?`/`#` are rejected here, not just left for the router layer: `next({ path })`
 * in the router guard only ever reads `.path`, discarding query/hash, so a value
 * like "/?tab=1" would resolve to the router's root route and re-enter the
 * redirect guard forever — vue-router's infinite-redirect detection only warns
 * in development, so in production this would recurse until the stack blows.
 * Rejecting the syntax outright prevents that unbounded loop regardless of which
 * routes happen to be registered.
 */
export function parseLandingPage(value) {
    if (typeof value !== "string") {
        return "/";
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return "/";
    }
    const isValidPath = trimmed.startsWith("/") && !trimmed.includes("?") && !trimmed.includes("#");
    if (!isValidPath) {
        logger.warn(`[configLoader] landingPage "${trimmed}" is not a bare absolute path (leading "/", no query, no fragment); falling back to "/".`);
        return "/";
    }
    return trimmed;
}
/**
 * Parses the public donor-site origin. Normalised HERE, once at load, rather than at the call
 * site: the consumer is a table-cell render function that runs per row on every re-render, and
 * the sibling parsers above establish load-time normalisation as this chain's convention.
 *
 * Anything that is not an absolute http(s) URL with a host and no query or fragment is treated
 * as unconfigured, returning `undefined` so the dependent link is simply not rendered:
 *
 *  - the value ends up in an `href`, so a ConfigMap typo must never be able to produce a
 *    `javascript:` or `data:` link;
 *  - a query or fragment silently breaks the URL the consumer builds on top of it — a base of
 *    `https://host/#/` (what copying the donor SPA's address bar gives you) would yield
 *    `https://host/#//forms/slug`, which loads the donor home page instead of the form. The
 *    sibling `parseLandingPage` rejects `?`/`#` for the same reason;
 *  - a hostless `https://` would survive a prefix test and then have both slashes stripped by
 *    the consumer's trailing-slash trim, producing `https:/forms/slug`.
 *
 * `http` stays allowed for any host, not just loopback: the documented local value is
 * `http://localhost:5273`, and a plain-http *deployed* host is an ops review concern rather than
 * something this parser should silently blank the column over.
 *
 * NOTE: `logger.warn` is dev-only (`LoggerService` gates every level on `!import.meta.env.PROD`),
 * so a misconfigured deployed environment gets no console trace — only an empty column. The
 * validation is still the load-bearing part; the log is a developer convenience.
 */
export function parsePublicFormBaseUrl(value) {
    if (typeof value !== "string") {
        return undefined;
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return undefined;
    }
    let parsed;
    try {
        parsed = new URL(trimmed);
    }
    catch {
        logger.warn(`[configLoader] publicFormBaseUrl "${trimmed}" is not a valid absolute URL; no public form link will be shown.`);
        return undefined;
    }
    const isHttp = parsed.protocol === "http:" || parsed.protocol === "https:";
    if (!isHttp || parsed.hostname === "" || parsed.search !== "" || parsed.hash !== "") {
        logger.warn(`[configLoader] publicFormBaseUrl "${trimmed}" must be an absolute http(s) URL with a host and no query or fragment; no public form link will be shown.`);
        return undefined;
    }
    return trimmed;
}
/**
 * Loads API configuration based on environment
 * @returns Promise resolving to the API configuration object
 */
export async function loadApiConfig() {
    if (import.meta.env.PROD) {
        try {
            const configFile = import.meta.env.VITE_API_CONFIG_FILE;
            if (configFile) {
                const configResponse = await fetch(configFile);
                if (configResponse.ok) {
                    const json = await configResponse.json();
                    return {
                        bffUrl: json.apiBffUrl,
                        unlayerProjectId: json.unlayerProjectId,
                        officeApiUrl: json.officeApiUrl,
                        supportContactUrl: json.supportContactUrl,
                        publicFormBaseUrl: parsePublicFormBaseUrl(json.publicFormBaseUrl),
                        orbitalEnabled: parseOrbitalEnabled(json.orbitalEnabled),
                        // Bundle, not `json` — see the CONSTRAINT on ApiConfig.appointmentPlanningEnabled.
                        appointmentPlanningEnabled: parseAppointmentPlanningEnabled(import.meta.env.VITE_APPOINTMENT_PLANNING_ENABLED),
                        spontaneousDonorsEnabled: parseSpontaneousDonorsEnabled(import.meta.env.VITE_SPONTANEOUS_DONORS_ENABLED),
                        businessRolesMockEnabled: parseBusinessRolesMockEnabled(json.businessRolesMockEnabled),
                        menuVisibleKeys: parseMenuVisibleKeys(json.menuVisibleKeys),
                        landingPage: parseLandingPage(json.landingPage),
                        editorImageMaxBytes: parseEditorImageMaxBytes(json.editorImageMaxBytes),
                    };
                }
            }
            logger.debug("Failed to fetch API config from config file");
        }
        catch (error) {
            logger.debug("Error loading API config:", error);
        }
    }
    return {
        bffUrl: import.meta.env.VITE_API_BFF_URL,
        unlayerProjectId: import.meta.env.VITE_UNLAYER_PROJECT_ID,
        officeApiUrl: import.meta.env.VITE_OFFICE_API_URL,
        supportContactUrl: import.meta.env.VITE_SUPPORT_CONTACT_URL,
        publicFormBaseUrl: parsePublicFormBaseUrl(import.meta.env.VITE_PUBLIC_FORM_BASE_URL),
        orbitalEnabled: parseOrbitalEnabled(import.meta.env.VITE_ORBITAL_ENABLED),
        appointmentPlanningEnabled: parseAppointmentPlanningEnabled(import.meta.env.VITE_APPOINTMENT_PLANNING_ENABLED),
        spontaneousDonorsEnabled: parseSpontaneousDonorsEnabled(import.meta.env.VITE_SPONTANEOUS_DONORS_ENABLED),
        businessRolesMockEnabled: parseBusinessRolesMockEnabled(import.meta.env.VITE_BUSINESS_ROLES_MOCK_ENABLED),
        menuVisibleKeys: parseMenuVisibleKeys(import.meta.env.VITE_MENU_VISIBLE_KEYS),
        landingPage: parseLandingPage(import.meta.env.VITE_LANDING_PAGE),
        editorImageMaxBytes: parseEditorImageMaxBytes(import.meta.env.VITE_EDITOR_IMAGE_MAX_BYTES),
    };
}
